import { BalanceData, PlayerProfile, raiseGear } from "@highmarch/domain";
import { OwnerView, SaveRecord, loadoutOf } from "@highmarch/app";
import { audio } from "../../audio/audio";
import { closeBtnHtml, fmtCompact, motionOk, tickNumber } from "../kit";
import { resourceIconUrl } from "../portraits";
import { ToastHandle, ToastOptions, toast } from "../toast";
import { guide } from "../guide/guide";
import {
  ArmScope, UpgradeOffer, dockModel, forOwner, initialOwner, neighbourOwner, newPerSocket, openSeen, ownerGroups, ownerKey, stripMode, upgradeOffers,
} from "./armouryModel";
import { dockHtml } from "./dock";
import { renderStrip } from "./forStrip";
import { flash } from "./fx";
import { refusal } from "./itemBits";
import type { ArmouryCtx } from "./host";
import { openItemCard } from "./itemCard";
import { levelUp } from "./itemFx";
import { closeUndo } from "./itemActions";
import { ownerStripHtml } from "./ownerStrip";
import { stageHtml } from "./paperDoll";
import { openSocketPicker } from "./picker";
import { renderStash } from "./stash";
import { ownerSwipe, revealScroll } from "./swipe";
import { essenceIconUrl, sigilIconUrl, sigilTotal, topSigil } from "../forgeArt";
import { fuseBadge } from "./forgeModel";
import { openFusion } from "./fusion";

/** What the Armoury needs from the game (main.ts): the model, the next level's scope, and the ways out. */
export interface ArmouryDeps {
  readonly B: BalanceData;
  readonly profile: PlayerProfile;
  readonly save: SaveRecord;
  /** The next level (readiness, the "Level N line-up") and the owned tower types. */
  readonly level: number;
  readonly roster: readonly boolean[];
  /** Save, and let the hub re-render its badges. */
  persist(): void;
  /** Start the next level (the hub's PLAY). */
  battle(): void;
  /** Open the hub's Towers tab. */
  openTowers(): void;
}


/** Resource counters in the header roll to a new value over this long (salvage, upgrades). */
const RES_TICK_MS = 600;
/** After a swipe, the owner strip scrolls so the selected tile sits this far (px) inside its left / right edge (the
 *  right edge fades out over the last 12%, about this much on a phone). */
const REVEAL_PAD_START_PX = 12;
const REVEAL_PAD_END_PX = 52;
/** The owner picked last (per session): the Gear tab reopens on it. */
let rememberedOwner: string | null = null;

/**
 * The Armoury (the Gear tab; concept /tmp/gearconcepts/armoury, judges' DECISION.md): Loadout — the owner strip, the
 * paper doll of the selected owner, the "For X" strip (or "Get stronger"), the readiness dock with Battle —
 * and the Stash tab (stash.ts). The sheet keeps the `inventory` class (SheetGuard's Gear tab). Pickers, item cards and
 * Clear junk open as children of this sheet (`ArmouryCtx.sheet`); a re-render replaces only the header and body.
 */
export function showArmoury(parent: HTMLElement, deps: ArmouryDeps): HTMLElement {
  const { B, profile, save } = deps;
  const scope: ArmScope = { level: deps.level, roster: deps.roster };
  const sheet = document.createElement("div");
  sheet.className = "sheet panel inventory armoury";
  const head = document.createElement("div");
  head.className = "arm-head";
  const body = document.createElement("div");
  body.className = "arm-body";
  const stash = document.createElement("div");
  stash.className = "arm-stash";
  sheet.append(head, body);

  // NEW is read against the watermark as the page opens; everything held is marked seen right away.
  // The level-gear gate's lesson is Captain Bram's now (guide scripts/hub.ts hub.gearGate, hub.gearGateEquip).
  const seen = openSeen(save, profile.gear);
  if (seen.changed) deps.persist();

  let tab: "loadout" | "stash" = "loadout";
  let forceStronger = false;
  let selected: string | null = null;
  /** Set by a swipe: the next render scrolls the owner strip to the newly selected tile. */
  let reveal = false;
  let shownGold: number | null = null;
  let shownEssence: number | null = null;

  const close = () => sheet.remove();
  /** The Armoury's own toasts: they go with the sheet (the UI style doc, O3). */
  const toasts = new Set<ToastHandle>();
  /** A toast over the lower half (above the dock), where the owner strip it would otherwise cover is not. */
  const lowToast = (text: string, o: ToastOptions = {}) => {
    const h = toast(parent, text, o);
    parent.lastElementChild?.classList.add("arm-low");
    toasts.add(h);
    return h;
  };
  const ctx: ArmouryCtx = {
    B, profile, scope, save, sheet, toastParent: parent,
    newSince: seen.since,
    changed: () => { deps.persist(); render(); },
    refresh: () => render(),
    focusOwner: (o) => { tab = "loadout"; selected = rememberedOwner = ownerKey(o); forceStronger = false; render(); },
    battle: () => { close(); deps.battle(); },
    openTowers: () => deps.openTowers(),
  };

  const headHtml = (): string => {
    const loose = profile.gear.filter((g) => g.equippedTo === null).length;
    const fresh = profile.gear.filter(seen.isNew).length;
    const res = `<span class="rp au" data-k="gold" aria-label="gold"><img decoding="async" src="${resourceIconUrl("gold")}" alt=""><b>${fmtCompact(shownGold ?? profile.gold)}</b></span>`;
    // Gear v2's forge goods beside the gold: Forge Essence and the sigils held (the rarest's medallion for the stack),
    // one button that opens the Forge; and the FUSE badge while the stash could fuse (forgeView fusableGroups).
    const sig = topSigil(profile.forge.sigils);
    const forge = `<button type="button" class="rp forgebtn" aria-label="Forge: ${profile.forge.essence} essence, ${sigilTotal(profile.forge.sigils)} sigils"><span class="ess"><img decoding="async" src="${essenceIconUrl()}" alt=""><b>${fmtCompact(shownEssence ?? profile.forge.essence)}</b></span>${sig !== null ? `<span class="sig"><img decoding="async" src="${sigilIconUrl(sig)}" alt=""><b>${sigilTotal(profile.forge.sigils)}</b></span>` : ""}</button>`;
    const fuse = fuseBadge(profile).count;
    const fuseChip = fuse > 0 ? `<button type="button" class="fusechip disp" aria-label="Fuse: ${fuse} group${fuse === 1 ? "" : "s"} ready">FUSE<i class="dot">${fuse}</i></button>` : "";
    return `<div class="ttl"><b class="disp">Armoury</b></div><div class="res">${res}${forge}${fuseChip}</div>
      <div class="seg2" role="tablist"><button type="button" role="tab" class="sg${tab === "loadout" ? " on" : ""}" data-t="loadout" aria-selected="${tab === "loadout"}">Loadout</button><button type="button" role="tab" class="sg${tab === "stash" ? " on" : ""}" data-t="stash" aria-selected="${tab === "stash"}">Stash <small>${loose}</small>${fresh > 0 ? `<i class="nchip">${fresh} new</i>` : ""}</button></div>
      ${closeBtnHtml()}`;
  };

  /** Rolls the header's gold and essence from what they showed to the profile's amounts (salvage, raise, undo). */
  const tickResources = () => {
    const now = profile.gold;
    const b = head.querySelector<HTMLElement>(".rp.au b");
    if (b && shownGold !== null && shownGold !== now) { tickNumber(b, shownGold, now, RES_TICK_MS, fmtCompact); flash(b.parentElement, "bump"); }
    shownGold = now;
    const ess = profile.forge.essence;
    const e = head.querySelector<HTMLElement>(".forgebtn .ess b");
    if (e && shownEssence !== null && shownEssence !== ess) { tickNumber(e, shownEssence, ess, RES_TICK_MS, fmtCompact); flash(e.parentElement, "bump"); }
    shownEssence = ess;
  };
  const openForge = () => openFusion(ctx, { openItem: (it) => openItemCard(ctx, it) });

  const renderHead = () => {
    head.innerHTML = headHtml();
    head.querySelector<HTMLButtonElement>(".close")!.onclick = close;
    head.querySelector<HTMLButtonElement>(".forgebtn")!.onclick = openForge;
    const fc = head.querySelector<HTMLButtonElement>(".fusechip");
    if (fc) fc.onclick = openForge;
    for (const b of head.querySelectorAll<HTMLButtonElement>(".seg2 .sg")) b.onclick = () => { tab = b.dataset.t as typeof tab; render(); };
    tickResources();
  };

  const selectedView = (views: readonly OwnerView[]): OwnerView => {
    const v = (selected !== null ? views.find((x) => ownerKey(x.owner) === selected) : undefined) ?? initialOwner(views, rememberedOwner);
    selected = ownerKey(v.owner);
    return v;
  };

  const renderLoadout = () => {
    const keepScroll = body.querySelector<HTMLElement>(".arm-owners")?.scrollLeft ?? null;
    const views = loadoutOf(profile, scope);
    const v = selectedView(views);
    const m = dockModel(profile, scope);
    const lineup = new Set(ownerGroups(views).lineup.map((x) => ownerKey(x.owner)));
    const strip = forOwner(profile, v, seen.isNew, (o) => lineup.has(ownerKey(o)));
    const mode = stripMode(profile, v, strip, forceStronger);
    body.className = "arm-body loadout";
    const fresh = newPerSocket(strip.items, (o) => lineup.has(ownerKey(o)));
    body.innerHTML = `${ownerStripHtml(views, selected!, deps.level)}${stageHtml(ctx, v, fresh)}<div class="arm-for"></div>${dockHtml(m, deps.level)}`;
    renderStrip(body.querySelector<HTMLElement>(".arm-for")!, ctx, v, strip, mode, upgradeOffers(profile, v), {
      onItem: (item) => openItemCard(ctx, item, { owner: v.owner }),
      onStash: () => { tab = "stash"; render(); },
      onUpgrade: (offer, card) => upgrade(offer, card),
      onTowers: () => ctx.openTowers(),
    });
    const owners = body.querySelector<HTMLElement>(".arm-owners")!;
    if (keepScroll !== null) owners.scrollLeft = keepScroll;
    else owners.querySelector<HTMLElement>(".otile.sel")?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    if (reveal) { reveal = false; revealSelected(owners); }
    const toBench = owners.querySelector<HTMLButtonElement>(".tobench");
    const bench = owners.querySelector<HTMLElement>(".og.bench");
    if (toBench && bench) toBench.onclick = () => {
      const left = Math.max(0, bench.offsetLeft - REVEAL_PAD_START_PX);
      if (typeof owners.scrollTo === "function") owners.scrollTo({ left, behavior: motionOk() ? "smooth" : "auto" });
      else owners.scrollLeft = left;
    };
    for (const t of owners.querySelectorAll<HTMLButtonElement>(".otile")) t.onclick = () => { selected = rememberedOwner = t.dataset.o!; forceStronger = false; render(); };
    if (v.owned) for (const s of body.querySelectorAll<HTMLButtonElement>(".arm-stage .sock")) s.onclick = () => openSocketPicker(ctx, v, Number(s.dataset.slot));
    body.querySelector<HTMLButtonElement>(".arm-dock .meter")!.onclick = () => { forceStronger = !forceStronger; render(); };
    body.querySelector<HTMLButtonElement>(".arm-dock .best")!.onclick = () => ctx.battle();
  };

  /** Scrolls the owner strip (only it, sideways) just enough to show the selected tile. */
  const revealSelected = (owners: HTMLElement) => {
    const t = owners.querySelector<HTMLElement>(".otile.sel");
    if (!t || typeof t.getBoundingClientRect !== "function") return;
    const box = owners.getBoundingClientRect(), r = t.getBoundingClientRect();
    const start = r.left - box.left + owners.scrollLeft;
    const left = revealScroll(owners.scrollLeft, owners.clientWidth, start, start + r.width, REVEAL_PAD_START_PX, REVEAL_PAD_END_PX);
    if (left === owners.scrollLeft) return;
    if (typeof owners.scrollTo === "function") owners.scrollTo({ left, behavior: motionOk() ? "smooth" : "auto" });
    else owners.scrollLeft = left;
  };

  // Swipe between owners on the paper doll, in the owner strip's order (locked types show their locked stage, as a
  // tap on their tile does). Wired once on the body, which outlives every re-render; only the Loadout stage starts it,
  // and not while a picker / card / Clear junk panel is open over the page.
  ownerSwipe(body, ".arm-stage", {
    canStart: () => tab === "loadout" && selected !== null && sheet.querySelector(".apanel") === null,
    hasNext: (step) => selected !== null && neighbourOwner(loadoutOf(profile, scope), selected, step) !== null,
    go: (step) => {
      const next = selected === null ? null : neighbourOwner(loadoutOf(profile, scope), selected, step);
      if (next === null) return;
      selected = rememberedOwner = next;
      forceStronger = false;
      reveal = true;
      render();
    },
  });

  const render = () => {
    renderHead();
    if (tab === "loadout") { stash.remove(); renderLoadout(); return; }
    body.className = "arm-body stashtab";
    const keep = stash.scrollTop;
    if (stash.parentElement !== body) { body.innerHTML = ""; body.append(stash); }
    stash.innerHTML = "";
    renderStash(stash, ctx);
    stash.scrollTop = keep;
  };

  /** "Get stronger": one item level on a worn item, inline (the stage's socket shines, the counters tick). */
  const upgrade = (o: UpgradeOffer, card: HTMLElement) => {
    if (!o.affordable) {
      lowToast(`Need ${o.cost - profile.forge.essence} more essence — salvage or feed spare gear, or win a chest`);
      flash(card, "nope");
      return;
    }
    const r = raiseGear(profile, o.item.id);
    if (r !== true) { toast(parent, refusal(r)); return; }
    audio.play("towerUpgrade");
    guide()?.signal("gearRaised");
    ctx.changed();
    const sock = body.querySelector<HTMLElement>(`.arm-stage .sock[data-slot="${o.item.slot}"]`);
    flash(sock, "shine");
    const disc = sock?.querySelector<HTMLElement>(".disc");
    if (disc) levelUp(disc, `LV ${o.item.level}`, o.item.rarity);
  };

  render();
  parent.append(sheet);
  // However the sheet goes (its ×, a tab tap, the hub's single-sheet rule removing it), an Undo it offered goes too: it
  // outlived the Armoury and covered the More rows. (toast.ts may later take an owner element and do this itself.)
  const gone = new MutationObserver(() => {
    if (sheet.isConnected) return;
    gone.disconnect();
    for (const h of toasts) h.dismiss();
    toasts.clear();
    closeUndo();
  });
  gone.observe(parent, { childList: true });
  return sheet;
}

